// The practitioner's room page for a per-minute consult.
//
// Mounts `ConsultRoomFrame` with NO token — the practitioner is identified by the owner
// session, exactly like `getConsultAccess`'s "omit token" convention — so the door the
// telehealth list's "open room" action and the per-minute branches of BookingsAdmin.jsx /
// booking/EventDetailDialog.jsx point at is a real room page that heartbeats for the
// whole visit, never a bare 302 to the provider.
//
// IT ALSO ENDS THE CONSULTATION. Completion used to live only on a row in the telehealth
// list, so the one person who knows the call is over had to leave the room to say so —
// and the meter, which stops at `practitioner_ended`, ran until they did. The button is
// here, and it opens the same `ConsultCompleteDialog` the list uses: one dialog, one set
// of three server calls, no second implementation to drift.
//
// THE STRIP IS THE SERVER'S VIEW, NOT A LOCAL CLOCK. `billableMinutes` is
// `sessions.compute_billable`'s answer from presence evidence and does not exist until
// the consult is closed out; `capturedCents` exists later still, once the capture lands
// (`schemas.SESSION_MONEY_FINAL_STATES`). `/status` answers `null` for each until its own
// instant, precisely so no surface renders "you were charged nothing" over a call that
// is running — or over one whose capture is still settling. A ticking counter here would
// be a second, unauthoritative meter that disagrees with the invoice, so the strip states
// only what the server has: nothing, then the minutes, then the minutes and the money.
//
// THAT RULE IS AMENDED, NOT BROKEN, BY THE LIFECYCLE PILLS BELOW. What it forbids is a
// browser INVENTING A QUANTITY: a billing meter measures elapsed presence and turns it
// into money, so a page computing one is writing a bill nobody authorised. A comparison
// against a server-published instant decides no amount at all — `endingSoonAt`,
// `scheduledEndAt` and `closesAt` are the server's own three instants, and all the page
// does with them is choose which sentence is on screen. The rule that survives, intact,
// is that nothing here may state a number of money, or of billable minutes, that the
// server has not stated.
//
// AND STILL NO TICKER, AND NO SECONDS. The pills are resolved at render from the status
// the frame pushes through `onStatus` — one poll, one clock, in one place, which is the
// frame's. A second interval here would repaint the practitioner's page under its own
// iframe once a second for a fact that changes once. Seconds are the patient's business:
// on this surface the phase is the whole content, plus a booked-end time and, for a solo
// run, whole minutes at the resolution `room_watch` actually offers.
//
// Route: `/admin/telehealth/consult/:appointmentId` (routes/adminRoutes.jsx and
// tenant/TenantSite.jsx, nested under the existing `/admin` layout — no sidebar entry of
// its own, reached only from a consult row).
import React, { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, AlertTriangle, Clock, Square, Users } from "lucide-react";
// Relative, not "@/public/...": lets the modules resolve for real under Jest (same
// convention every other gateway-consuming component in this build uses — see
// gateway/__testutils__/mocks.js), rather than adding one more "@/" path to stub.
// `fmtClock` travels with the frame deliberately: it is the SAME formatter the patient's
// screen uses for the same instant, so the two people in one call read the booked end in
// their own locales and never in two different renderings of one time.
import ConsultRoomFrame, { fmtClock } from "../../public/ConsultRoomFrame";
import ConsultCompleteDialog from "./ConsultCompleteDialog";
import { fmtMoney } from "../money";
// `SESSION_MONEY_FINAL_STATES` (contract.js, mirroring `schemas.py` exactly — pinned by
// `i18n/__tests__/keyCoverage.test.js`) is the closed set of session states in which the
// money is settled and the session is closed; the same three `consultBuckets.js` files
// under "done". Imported rather than hand-spelled because what this gates is a WRITE, and
// a second, independent copy of the same three literals is exactly how one of the three
// sites (this page, `useConsultSession.js`, `useConsultStatus.js`) used to drift from the
// other two.
import { SESSION_MONEY_FINAL_STATES, isFixedBlock } from "../../gateway/contract";
import { roomPhase } from "../../gateway/roomPhase";
import { adminHref } from "../adminPath";

export default function ConsultRoomPage() {
  const t = useT("admin");
  const { lang } = useLanguage();
  const { appointmentId } = useParams();

  // Fed by the frame's own `/consults/{id}/status` poll (it already runs one; a second
  // poll from this page would ask the same question twice and could answer differently).
  const [status, setStatus] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);

  // STABLE: the frame fires this from an effect keyed on the prop, so a new function per
  // render would re-fire it on every render.
  const handleStatus = useCallback((next) => { setStatus(next || null); }, []);

  const sessionStatus = status?.sessionStatus || null;
  const isTerminal = SESSION_MONEY_FINAL_STATES.includes(sessionStatus);
  const capReached = status?.capReachedAt != null;
  // THREE RENDERINGS, because the server publishes the two figures at two different
  // instants. `billableMinutes` arrives with the computation (a measurement of the call);
  // `capturedCents` arrives a Stripe round trip later with the capture, and stays null in
  // between — and for good if that capture is refused. `!= null` throughout, never
  // truthiness: a genuine zero capture is a FINDING (nobody was ever both-present, the
  // hold is released) and must be stated.
  //
  // THE AMOUNT IS NEVER RENDERED FROM A NULL. `fmtMoney(null)` prints "0.00", so a strip
  // that showed the amount whenever the minutes existed said "Billed: 2 min · 0.00 USD"
  // over a consultation that was about to be charged 20.00 — "you were charged nothing",
  // stated to the one person who could still act on it.
  //
  // NOR IS IT EVER RENDERED FROM A GUESSED CURRENCY. `status.currency` is `/status`'s own
  // field (`schemas.payment_row`'s wire shape); a client-side fallback to a hardcoded
  // default (there was one here — `SETTLEMENT_CURRENCY`) would print a currency code the
  // server never actually stated the instant that field is ever absent, which is exactly
  // as unstateable as the amount itself being null. A captured figure with no currency
  // therefore renders the same "pending" copy the null-amount case does, never a guess.
  const minutes = status?.billableMinutes;
  const captured = status?.capturedCents;
  const currency = status?.currency;
  const billed = minutes != null && captured != null && !!currency;
  const measuredOnly = minutes != null && !billed;
  // WHERE IN ITS LIFE IS THIS ROOM? Resolved AT RENDER, from the same pure module the
  // patient's frame reads, so the practitioner and their client never see one call
  // described two ways. `Date.now()` uncorrected here on purpose: the frame owns the
  // server-offset correction because it owns the poll, and this page re-renders on every
  // `onStatus` push anyway — a second, independently corrected clock on the same screen
  // would be one more thing that can disagree, for a surface that renders no seconds.
  const phase = roomPhase(status, Date.now());
  const endsAtClock = phase.endsAt ? fmtClock(phase.endsAt, lang) : "";
  // WHOLE MINUTES, at the resolution `room_watch` offers and no finer: the presence
  // snapshot behind it is up to 60 s old. `Math.max(1, …)` because below one minute this
  // statement has no resolution left, and "0 min" names an instant that has already
  // passed while the participant is demonstrably still in the room.
  const soloMinutesLeft = (phase.solo.active && phase.solo.secondsLeft != null)
    ? Math.max(1, Math.ceil(phase.solo.secondsLeft / 60))
    : null;

  // A FIXED BLOCK HAS NO METER, so none of the three money states above describes it and
  // the "billed on completion" fallback would promise a charge that already happened weeks
  // ago. Its own facts are the appointment's: the block was paid at booking, and the window
  // closes on its own. There is nothing for the practitioner to end, either — `completed`
  // is written when the door shuts, not by a hand — so the button goes with the meter.

  return (
    <div data-testid="consult-room-admin-page" className="space-y-4">
      <Link
        to={adminHref("/admin/telehealth")}
        data-testid="consult-room-back"
        className="inline-flex items-center gap-1.5 text-sm text-eh-sage-deep hover:underline"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("tele.title")}
      </Link>

      <div
        className="bg-white rounded-2xl border border-eh-border-soft px-5 py-4 flex flex-wrap items-center gap-4"
        data-testid="consult-room-admin-strip"
      >
        {/* A FIXED BLOCK HAS NO SESSION, AND ITS STATE IS ITS APPOINTMENT'S. `sessionStatus`
            is permanently null for one, so this rendered a bare em dash — no state at all
            — on the practitioner's own room page, for a consultation whose status the
            list beside it prints as a pill. The same `bookings.statusLabel.*` family, so
            the two screens cannot disagree about one appointment. */}
        <span className="text-sm font-medium text-eh-ink" data-testid="consult-room-admin-status">
          {sessionStatus
            ? t(`consult.list.status.${sessionStatus}`)
            : (isFixedBlock(status) && status?.bookingStatus
                ? t(`bookings.statusLabel.${status.bookingStatus}`, null, status.bookingStatus)
                : "—")}
        </span>

        {capReached && (
          <span
            className="inline-flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-2.5 py-1"
            data-testid="consult-room-admin-cap"
            role="status"
          >
            <AlertTriangle className="w-3.5 h-3.5" /> {t("consult.room.admin.capReached")}
          </span>
        )}

        {/* THE LIFECYCLE PILLS. Each is gated on the instant it names, because a null on
            the wire REMOVES a boundary and never licenses computing one here — the hard
            close is `scheduledEndAt + hardCloseGraceMinutes` and that grace is an
            operator setting this page does not hold. */}
        {phase.phase === "endingSoon" && !!endsAtClock && (
          <span
            className="inline-flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-2.5 py-1"
            data-testid="consult-room-admin-ending-soon"
            role="status"
          >
            <Clock className="w-3.5 h-3.5" /> {t("consult.room.admin.endingSoon", { when: endsAtClock })}
          </span>
        )}

        {phase.phase === "overrun" && !!endsAtClock && (
          <span
            className="inline-flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-2.5 py-1"
            data-testid="consult-room-admin-overrun"
            role="status"
          >
            <Clock className="w-3.5 h-3.5" /> {t("consult.room.admin.overrun", { when: endsAtClock })}
          </span>
        )}

        {/* ORTHOGONAL TO THE PHASE — someone can be alone in a call that is live, ending
            soon or already past its booked end, and both facts are worth a pill. */}
        {phase.phase !== "closed" && soloMinutesLeft != null && (
          <span
            className="inline-flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-2.5 py-1"
            data-testid="consult-room-admin-solo"
            role="status"
          >
            <Users className="w-3.5 h-3.5" /> {t("consult.room.admin.solo", { minutes: soloMinutesLeft })}
          </span>
        )}

        {/* NO INSTANT INTERPOLATED: this pill is also the answer when the phase closed
            because the SESSION ended rather than because a clock ran out, and there is
            no `closesAt` to name in that case. */}
        {phase.phase === "closed" && (
          <span
            className="inline-flex items-center gap-1.5 text-xs text-eh-muted bg-eh-cream border border-eh-border-soft rounded-full px-2.5 py-1"
            data-testid="consult-room-admin-closed"
            role="status"
          >
            <Clock className="w-3.5 h-3.5" /> {t("consult.room.admin.roomClosed")}
          </span>
        )}

        {billed && (
          <span className="text-sm text-eh-ink tabular-nums" data-testid="consult-room-admin-billed">
            {t("consult.room.admin.billed", { minutes, amount: fmtMoney(captured, currency) })}
          </span>
        )}

        {measuredOnly && (
          <span className="text-sm text-eh-ink tabular-nums" data-testid="consult-room-admin-minutes">
            {t("consult.room.admin.billedPending", { minutes })}
          </span>
        )}

        {isFixedBlock(status) && (
          <span className="text-xs text-eh-muted" data-testid="consult-room-admin-block">
            {t("consult.room.admin.fixedBlock")}
          </span>
        )}

        {!isFixedBlock(status) && !billed && !measuredOnly && (
          <span className="text-xs text-eh-muted" data-testid="consult-room-admin-pending">
            {t("consult.room.admin.meterOnCompletion")}
          </span>
        )}

        {!isFixedBlock(status) && (
          <Button
            size="sm"
            className="ml-auto"
            onClick={() => setCompleteOpen(true)}
            disabled={isTerminal}
            data-testid="consult-room-admin-end"
          >
            <Square className="w-3.5 h-3.5 mr-1.5" /> {t("consult.room.admin.end")}
          </Button>
        )}
      </div>

      <ConsultRoomFrame appointmentId={appointmentId} role="practitioner" onStatus={handleStatus} />

      <ConsultCompleteDialog
        appointmentId={appointmentId}
        open={completeOpen}
        onClose={() => setCompleteOpen(false)}
      />
    </div>
  );
}
